import { useEffect, useState } from 'react';

import apiToken, {
  handleApiError,
  isSuccess,
} from '@/shared/services/axiosInstance';

import { SignUpForm } from '../types/SignUpTypes';

interface useEmailProps {
  signupForm: SignUpForm;
  setSignupForm: React.Dispatch<React.SetStateAction<SignUpForm>>;
}

export const useEmail = ({ signupForm, setSignupForm }: useEmailProps) => {
  const [emailSent, setEmailSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState('');
  const [timer, setTimer] = useState(180); // 3분 = 180초
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isTimerRunning && timer > 0) {
      const timeout = setTimeout(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setIsTimerRunning(false);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isTimerRunning, timer]);

  const checkEmailDuplicate = async () => {
    try {
      const response = await apiToken.post('/api/v1/auth/email-duplication', {
        email: signupForm.email,
      });
      if (isSuccess(response.data)) {
        // 이메일 사용 가능하면 인증코드 발송 함수 실행
        sendEmailVerificationCode();
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);

      if (errorMessage === 'The user already exists') {
        alert('The user already exists.');
        return;
      }

      console.error('Failed to check email duplication:', errorMessage);
    }
  };
  const sendEmailVerificationCode = async () => {
    setEmailSent(true);
    setTimer(180);
    setIsTimerRunning(true);
    setIsEmailVerified(false);
    try {
      await apiToken.post('/api/v1/mail', { email: signupForm.email });
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to send email verification code:', errorMessage);
      alert(
        'We could not send the verification email. Please check your email address and try again.',
      );
    }
  };

  const verifyEmailCode = async () => {
    try {
      const response = await apiToken.post('/api/v1/auth/email-verification', {
        email: signupForm.email,
        code: verificationCode,
      });
      if (isSuccess(response.data)) {
        setIsEmailVerified(true);
        setIsTimerRunning(false);
        setTimer(0);
        setEmailVerificationError('');
      } else if (
        response.data.statusCode === 400 &&
        response.data.message === 'Validation failed' &&
        response.data.details &&
        response.data.details.some((d: string) =>
          d.includes('Verification code'),
        )
      ) {
        setEmailVerificationError('Wrong code');
      } else if (
        response.data.statusCode === 401 &&
        response.data.message === 'Invalid email verification code.'
      ) {
        setEmailVerificationError('Wrong code');
      } else {
        setEmailVerificationError('');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to verify email code:', errorMessage);
      setEmailVerificationError('Wrong code');
    }
  };
  return {
    emailSent,
    isEmailVerified,
    emailVerificationError,
    timer,
    isTimerRunning,
    verificationCode,
    setVerificationCode,
    setIsTimerRunning,
    setTimer,
    formatTime,
    checkEmailDuplicate,
    sendEmailVerificationCode,
    verifyEmailCode,
  };
};
